import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore'
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js'
import { Bar, Doughnut, Line } from 'react-chartjs-2'
import { db } from '../firebase'
import './FireflyReportsComp.css'

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Tooltip, Legend, Filler)

const normalizeList = (items = [], limit = 8) =>
  [...new Set((Array.isArray(items) ? items : []).map((item) => String(item || '').trim()).filter(Boolean))].slice(0, limit)

const normalizeDiagnoses = (items = [], limit = 6) =>
  (Array.isArray(items) ? items : [])
    .map((item) => ({
      topic: String(item?.topic || '').trim(),
      subtopic: String(item?.subtopic || '').trim(),
      misconception: String(item?.misconception || '').trim(),
      evidence: String(item?.evidence || '').trim(),
      bestIntervention: String(item?.bestIntervention || item?.best_intervention || '').trim(),
    }))
    .filter((item) => item.topic || item.subtopic || item.misconception)
    .slice(0, limit)

const formatDateTime = (value) => {
  const timestamp = Number(value) || 0
  if (!timestamp) return 'Not available'

  return new Intl.DateTimeFormat('en-KE', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(timestamp))
}

const formatShortDate = (value) => {
  const timestamp = Number(value) || 0
  if (!timestamp) return 'Session'

  return new Intl.DateTimeFormat('en-KE', {
    month: 'short',
    day: 'numeric',
  }).format(new Date(timestamp))
}

const formatDuration = (value) => {
  const totalSeconds = Math.max(0, Number(value) || 0)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  if (minutes <= 0) return `${seconds}s`
  if (seconds === 0) return `${minutes} min`
  return `${minutes} min ${seconds}s`
}

const getSignalScore = (value) => {
  const signal = String(value || '').toLowerCase()
  if (!signal) return 50
  if (signal.includes('high') || signal.includes('confident') || signal.includes('strong')) return 86
  if (signal.includes('improv') || signal.includes('rising') || signal.includes('better')) return 72
  if (signal.includes('steady') || signal.includes('stable')) return 58
  if (signal.includes('low') || signal.includes('drop') || signal.includes('uncertain')) return 34
  return 50
}

const getTopCounts = (items = [], limit = 6) => {
  const counts = items.reduce((acc, item) => {
    const label = String(item || '').trim()
    if (!label) return acc
    acc[label] = (acc[label] || 0) + 1
    return acc
  }, {})

  return Object.entries(counts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label))
    .slice(0, limit)
}

const getProfileFitScore = (value) => {
  const numericValue = Number(value)
  if (!Number.isNaN(numericValue) && numericValue > 0) {
    return Math.max(0, Math.min(100, numericValue))
  }

  const signal = String(value || '').toLowerCase()
  if (!signal) return 50
  if (signal.includes('high') || signal.includes('strong') || signal.includes('best')) return 88
  if (signal.includes('medium') || signal.includes('moderate') || signal.includes('helpful')) return 64
  if (signal.includes('low') || signal.includes('limited') || signal.includes('weak')) return 36
  return 50
}

const getProfileBullets = (items = [], limit = 4) =>
  (Array.isArray(items) ? items : [])
    .map((item) => {
      if (typeof item === 'string') return { label: item, evidence: '', action: '' }
      return {
        label: String(item?.label || item?.title || item?.theme || '').trim(),
        evidence: String(item?.evidence || item?.reason || item?.description || '').trim(),
        action: String(item?.action || item?.nextStep || item?.guidance || item?.firstMove || '').trim(),
      }
    })
    .filter((item) => item.label || item.evidence || item.action)
    .slice(0, limit)

const emptyChartLabel = ['No data yet']
const LEARNING_PROFILE_API_BASE = (
  process.env.REACT_APP_LEARNING_PROFILE_API_URL
  || 'https://us-central1-ubunifu-college.cloudfunctions.net/learningProfilesApi'
).replace(/\/$/, '')

const chartTextColor = '#5f7388'
const chartGridColor = 'rgba(134, 158, 184, 0.18)'

const baseChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        color: chartTextColor,
        boxWidth: 10,
        boxHeight: 10,
        usePointStyle: true,
      },
    },
  },
}

function FireflyReportsComp() {
  const auth = useMemo(() => getAuth(), [])
  const [currentUser, setCurrentUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [generatedProfileDoc, setGeneratedProfileDoc] = useState(null)
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshingProfile, setRefreshingProfile] = useState(false)
  const [error, setError] = useState('')
  const [activeView, setActiveView] = useState('overview')
  const [selectedReportId, setSelectedReportId] = useState('')

  const loadReports = useCallback(async (studentId) => {
    if (!studentId) return

    setLoading(true)
    setError('')

    try {
      const sessionsQuery = query(
        collection(db, 'plm', studentId, 'sessions'),
        orderBy('startedAtMs', 'desc')
      )
      const [profileSnap, sessionsSnap, generatedProfileSnap] = await Promise.all([
        getDoc(doc(db, 'plm', studentId)),
        getDocs(sessionsQuery),
        getDoc(doc(db, 'studentFireflyLearningProfiles', studentId)),
      ])

      const nextProfile = profileSnap.exists()
        ? {
            summary: String(profileSnap.data()?.lastSessionSummary || '').trim(),
            strengths: normalizeList(profileSnap.data()?.strengths, 6),
            struggles: normalizeList(profileSnap.data()?.struggles, 6),
            misconceptions: normalizeList(profileSnap.data()?.misconceptions, 6),
            diagnoses: normalizeDiagnoses(profileSnap.data()?.diagnoses, 6),
            preferredTeachingModes: normalizeList(profileSnap.data()?.preferredTeachingModes, 6),
            supportStrategies: normalizeList(profileSnap.data()?.supportStrategies, 6),
            recommendedNextSteps: normalizeList(profileSnap.data()?.recommendedNextSteps, 6),
            learningPace: String(profileSnap.data()?.learningPace || '').trim(),
            confidenceTrend: String(profileSnap.data()?.confidenceTrend || '').trim(),
            topicsSeen: normalizeList(profileSnap.data()?.topicsSeen, 10),
            currentLessonObjective: String(profileSnap.data()?.currentLessonObjective || '').trim(),
          }
        : null

      const nextReports = sessionsSnap.docs.map((sessionDoc) => {
        const data = sessionDoc.data() || {}
        return {
          id: sessionDoc.id,
          status: String(data.status || '').trim(),
          summary: String(data.summary || '').trim(),
          sessionFocus: String(data.sessionFocus || '').trim(),
          startedAtMs: Number(data.startedAtMs) || 0,
          endedAtMs: Number(data.endedAtMs) || 0,
          durationSeconds: Number(data.durationSeconds) || 0,
          topicsCovered: normalizeList(data.topicsCovered, 8),
          strengths: normalizeList(data.strengths, 6),
          struggles: normalizeList(data.struggles, 6),
          misconceptions: normalizeList(data.misconceptions, 6),
          diagnoses: normalizeDiagnoses(data.diagnoses, 6),
          supportStrategies: normalizeList(data.supportStrategies, 6),
          recommendedNextSteps: normalizeList(data.recommendedNextSteps, 6),
          preferredTeachingModes: normalizeList(data.preferredTeachingModes, 6),
          teachingMethodsUsed: normalizeList(data.teachingMethodsUsed, 6),
          confidenceTrend: String(data.confidenceTrend || '').trim(),
          learningPace: String(data.learningPace || '').trim(),
        }
      }).filter((report) => report.status !== 'started')

      setProfile(nextProfile)
      setGeneratedProfileDoc(generatedProfileSnap.exists() ? { id: generatedProfileSnap.id, ...generatedProfileSnap.data() } : null)
      setReports(nextReports)
      setSelectedReportId((currentId) => nextReports.some((report) => report.id === currentId) ? currentId : '')
    } catch (loadError) {
      setError(loadError?.message || 'Firefly could not load your reports right now.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null)
      if (user?.uid) {
        loadReports(user.uid)
      } else {
        setProfile(null)
        setGeneratedProfileDoc(null)
        setReports([])
        setSelectedReportId('')
        setLoading(false)
      }
    })

    return () => unsubscribe()
  }, [auth, loadReports])

  const selectedReport = selectedReportId ? reports.find((report) => report.id === selectedReportId) || null : null

  const handleRefreshReports = useCallback(async () => {
    if (!currentUser?.uid) return

    setRefreshingProfile(true)
    setError('')

    try {
      const idToken = await currentUser.getIdToken(true)
      const response = await fetch(`${LEARNING_PROFILE_API_BASE}/profiles/rebuild`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          studentId: currentUser.uid,
          source: 'firefly',
          force: true
        })
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        throw new Error(payload?.error || 'Firefly could not rebuild your general report right now.')
      }

      await loadReports(currentUser.uid)
    } catch (refreshError) {
      console.error('Error rebuilding Firefly learning profile:', refreshError)
      setError(refreshError?.message || 'Firefly could not rebuild your general report right now.')
    } finally {
      setRefreshingProfile(false)
    }
  }, [currentUser, loadReports])

  const analytics = useMemo(() => {
    const chronologicalReports = [...reports].sort((a, b) => (a.startedAtMs || 0) - (b.startedAtMs || 0))
    const recentReports = chronologicalReports.slice(-8)
    const topicItems = reports.flatMap((report) => [
      ...(report.topicsCovered || []),
      ...(report.diagnoses || []).flatMap((item) => [item.topic, item.subtopic]),
    ])
    const topTopics = getTopCounts(topicItems, 8)
    const modeCounts = getTopCounts(reports.flatMap((report) => report.preferredTeachingModes || []), 5)
    const totalSeconds = reports.reduce((sum, report) => sum + (Number(report.durationSeconds) || 0), 0)
    const totalTopics = new Set(topicItems.map((item) => String(item || '').trim()).filter(Boolean)).size
    const totalStrengths = reports.reduce((sum, report) => sum + (report.strengths?.length || 0), 0)
    const totalStruggles = reports.reduce((sum, report) => sum + (report.struggles?.length || 0), 0)
    const totalDiagnoses = reports.reduce((sum, report) => sum + (report.diagnoses?.length || 0), 0)

    return {
      chronologicalReports,
      recentReports,
      topTopics,
      modeCounts,
      totalSeconds,
      totalTopics,
      totalStrengths,
      totalStruggles,
      totalDiagnoses,
      averageDuration: reports.length ? Math.round(totalSeconds / reports.length) : 0,
    }
  }, [reports])

  const generatedProfile = useMemo(() => {
    if (!generatedProfileDoc?.profile) return null

    const profileData = generatedProfileDoc.profile || {}
    const profileMeta = generatedProfileDoc.profileMeta || {}
    const analyticsData = generatedProfileDoc.analytics || {}

    return {
      headline: String(profileData.summary?.headline || '').trim() || 'Firefly learning profile',
      learnerSnapshot: String(profileData.summary?.learnerSnapshot || profileData.summary?.momentumNarrative || '').trim(),
      futureOfLearningLens: String(profileData.summary?.futureOfLearningLens || '').trim(),
      momentumNarrative: String(profileData.summary?.momentumNarrative || '').trim(),
      confidence: String(profileData.summary?.confidence || '').trim() || 'Building signal',
      confidenceReason: String(profileData.summary?.confidenceReason || '').trim(),
      readinessBand: String(profileData.profileSignals?.readinessBand || '').trim() || 'Building profile',
      evidenceStrength: String(profileMeta.evidenceStrength || '').trim() || 'low',
      evidenceSummary: String(profileMeta.summary || '').trim(),
      sourceCount: Number(profileMeta.sourceCount) || Number(analyticsData.sessionCount) || reports.length,
      lastAnalyzedLabel: formatDateTime(
        generatedProfileDoc.lastAnalyzedAtMs
        || generatedProfileDoc.updatedAtMs
        || generatedProfileDoc.generatedAtMs
        || profileMeta.latestSourceAtMs
      ),
      updatedLabel: formatDateTime(
        generatedProfileDoc.updatedAtMs
        || generatedProfileDoc.generatedAtMs
        || profileMeta.latestSourceAtMs
      ),
      learningModes: (Array.isArray(profileData.profileSignals?.learningModeFit) ? profileData.profileSignals.learningModeFit : [])
        .map((item) => ({
          mode: String(item?.mode || '').trim(),
          fit: String(item?.fit || '').trim(),
          evidence: String(item?.evidence || '').trim(),
          score: getProfileFitScore(item?.fit),
        }))
        .filter((item) => item.mode || item.fit || item.evidence),
      changeSinceLastUpdate: {
        summary: String(profileData.changeSinceLastUpdate?.summary || '').trim(),
        newStrengths: normalizeList(profileData.changeSinceLastUpdate?.newStrengths, 4),
        newRisks: normalizeList(profileData.changeSinceLastUpdate?.newRisks, 4),
        improvedSignals: normalizeList(profileData.changeSinceLastUpdate?.improvedSignals, 4),
        declinedSignals: normalizeList(profileData.changeSinceLastUpdate?.declinedSignals, 4),
      },
      sessionIntelligence: {
        preferredTeachingModes: normalizeList(profileData.sessionIntelligence?.preferredTeachingModes, 4),
        explanationFormatsThatWork: normalizeList(profileData.sessionIntelligence?.explanationFormatsThatWork, 4),
        frictionTriggers: normalizeList(profileData.sessionIntelligence?.frictionTriggers, 4),
        engagementPattern: String(profileData.sessionIntelligence?.engagementPattern || '').trim(),
        pacePattern: String(profileData.sessionIntelligence?.pacePattern || '').trim(),
        confidencePattern: String(profileData.sessionIntelligence?.confidencePattern || '').trim(),
      },
      conceptMasteryMap: {
        strongestThemes: getProfileBullets(profileData.conceptMasteryMap?.strongestThemes, 4),
        fragileThemes: getProfileBullets(profileData.conceptMasteryMap?.fragileThemes, 4),
        misconceptionThreads: getProfileBullets(profileData.conceptMasteryMap?.misconceptionThreads, 4),
      },
      diagnosticLens: {
        misconceptionRecurrence: getProfileBullets(profileData.diagnosticLens?.misconceptionRecurrence, 4),
      },
      studentActionPlan: {
        keepDoing: normalizeList(profileData.studentActionPlan?.keepDoing, 4),
        focusNextSessions: normalizeList(profileData.studentActionPlan?.focusNextSessions, 4),
        beforeNextSession: normalizeList(profileData.studentActionPlan?.beforeNextSession, 4),
        nextChallenge: String(profileData.studentActionPlan?.nextChallenge || '').trim(),
        reflectionPrompt: String(profileData.studentActionPlan?.reflectionPrompt || '').trim(),
      },
      studentFacing: {
        encouragement: String(profileData.studentFacing?.encouragement || '').trim(),
        whatIsGoingWell: normalizeList(profileData.studentFacing?.whatIsGoingWell, 4),
        focusNext: normalizeList(profileData.studentFacing?.focusNext, 4),
        whyThisMatters: String(profileData.studentFacing?.whyThisMatters || '').trim(),
        reflectionPrompt: String(profileData.studentFacing?.reflectionPrompt || '').trim(),
      },
      fireflyRecommendation: {
        shouldInviteLiveSession: Boolean(profileData.fireflyRecommendation?.shouldInviteLiveSession),
        urgency: String(profileData.fireflyRecommendation?.urgency || '').trim() || 'low',
        studentSummary: String(profileData.fireflyRecommendation?.studentSummary || '').trim(),
        outreachReason: String(profileData.fireflyRecommendation?.outreachReason || profileData.fireflyRecommendation?.teacherReason || '').trim(),
        focusConcepts: getProfileBullets(profileData.fireflyRecommendation?.recommendedFocusConcepts, 4),
        sessionGoals: normalizeList(profileData.fireflyRecommendation?.sessionGoals, 4),
        inviteWithin: String(profileData.fireflyRecommendation?.inviteWithin || '').trim(),
      },
      tutoringPlaybook: {
        openingMoves: normalizeList(profileData.tutoringPlaybook?.openingMoves, 4),
        inSessionMoves: normalizeList(profileData.tutoringPlaybook?.inSessionMoves, 4),
        checksForUnderstanding: normalizeList(profileData.tutoringPlaybook?.checksForUnderstanding, 4),
        boardAndVisualStrategies: normalizeList(profileData.tutoringPlaybook?.boardAndVisualStrategies, 4),
      },
    }
  }, [generatedProfileDoc, reports.length])

  const rhythmData = useMemo(() => {
    const labels = analytics.recentReports.length
      ? analytics.recentReports.map((report) => formatShortDate(report.endedAtMs || report.startedAtMs))
      : emptyChartLabel
    const durations = analytics.recentReports.length
      ? analytics.recentReports.map((report) => Math.max(1, Math.round((report.durationSeconds || 0) / 60)))
      : [0]
    const confidence = analytics.recentReports.length
      ? analytics.recentReports.map((report) => getSignalScore(report.confidenceTrend))
      : [0]

    return {
      labels,
      datasets: [
        {
          label: 'Minutes',
          data: durations,
          borderColor: '#3c6f93',
          backgroundColor: 'rgba(60, 111, 147, 0.12)',
          pointBackgroundColor: '#3c6f93',
          tension: 0.35,
          fill: true,
        },
        {
          label: 'Confidence signal',
          data: confidence,
          borderColor: '#d96f32',
          backgroundColor: 'rgba(217, 111, 50, 0.12)',
          pointBackgroundColor: '#d96f32',
          tension: 0.35,
        },
      ],
    }
  }, [analytics.recentReports])

  const topicData = useMemo(() => ({
    labels: analytics.topTopics.length ? analytics.topTopics.map((item) => item.label) : emptyChartLabel,
    datasets: [
      {
        label: 'Mentions',
        data: analytics.topTopics.length ? analytics.topTopics.map((item) => item.value) : [0],
        backgroundColor: ['#2f6f73', '#d96f32', '#725f42', '#5c7f43', '#3c6f93', '#bb9c46', '#58708f', '#9a6f55'],
        borderRadius: 8,
      },
    ],
  }), [analytics.topTopics])

  const signalData = useMemo(() => ({
    labels: ['Strengths', 'Challenges', 'Diagnoses'],
    datasets: [
      {
        data: [
          analytics.totalStrengths || 0,
          analytics.totalStruggles || 0,
          analytics.totalDiagnoses || 0,
        ],
        backgroundColor: ['#3c6f93', '#d96f32', '#58708f'],
        borderColor: '#ffffff',
        borderWidth: 4,
      },
    ],
  }), [analytics.totalDiagnoses, analytics.totalStrengths, analytics.totalStruggles])

  const modeData = useMemo(() => {
    if (generatedProfile?.learningModes?.length) {
      return {
        labels: generatedProfile.learningModes.map((item) => item.mode),
        datasets: [
          {
            label: 'Fit score',
            data: generatedProfile.learningModes.map((item) => item.score),
            backgroundColor: ['#5c7f43', '#3c6f93', '#d96f32', '#725f42', '#58708f', '#2f6f73'],
            borderRadius: 8,
          },
        ],
      }
    }

    return {
      labels: analytics.modeCounts.length ? analytics.modeCounts.map((item) => item.label) : emptyChartLabel,
      datasets: [
        {
          label: 'Sessions',
          data: analytics.modeCounts.length ? analytics.modeCounts.map((item) => item.value) : [0],
          backgroundColor: '#5c7f43',
          borderRadius: 8,
        },
      ],
    }
  }, [analytics.modeCounts, generatedProfile])

  const lineOptions = useMemo(() => ({
    ...baseChartOptions,
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: chartTextColor },
      },
      y: {
        beginAtZero: true,
        grid: { color: chartGridColor },
        ticks: { color: chartTextColor },
      },
    },
  }), [])

  const barOptions = useMemo(() => ({
    ...baseChartOptions,
    indexAxis: 'y',
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: chartGridColor },
        ticks: { color: chartTextColor, precision: 0 },
      },
      y: {
        grid: { display: false },
        ticks: { color: chartTextColor },
      },
    },
  }), [])

  const doughnutOptions = useMemo(() => ({
    ...baseChartOptions,
    cutout: '68%',
  }), [])

  if (loading) {
    return (
      <div className='fireflyReportsPage'>
        <section className='fireflyReportsHero'>
          <span className='fireflyReportsEyebrow'>My Firefly Reports</span>
          <h1>Loading your learning reports...</h1>
          <p>Firefly is pulling together your learner profile and past session reflections.</p>
        </section>
      </div>
    )
  }

  if (!currentUser) {
    return (
      <div className='fireflyReportsPage'>
        <section className='fireflyReportsHero'>
          <span className='fireflyReportsEyebrow'>My Firefly Reports</span>
          <h1>Sign in to view your reports.</h1>
          <p>Your Firefly lesson reports appear here after each tutoring session.</p>
        </section>
      </div>
    )
  }

  const renderReportDetail = (report) => {
    if (!report) {
      return (
        <div className='fireflyReportsEmpty'>
          <h3>No report selected</h3>
          <p>Finish a Firefly tutoring session and a detailed individual report will appear here.</p>
        </div>
      )
    }

    return (
      <article className='fireflyReportsDetail'>
        <div className='fireflyReportsSectionHead'>
          <div>
            <span className='fireflyReportsTag'>Individual Report</span>
            <h2>{report.sessionFocus || 'General Firefly lesson'}</h2>
            <p>{formatDateTime(report.endedAtMs || report.startedAtMs)}</p>
          </div>
          <div className='fireflyReportsSessionStats'>
            <span>{formatDuration(report.durationSeconds)}</span>
            <span>{report.confidenceTrend || 'steady confidence'}</span>
          </div>
        </div>

        <div className='fireflyReportsMetaGrid'>
          <div className='fireflyReportsMetaStat'>
            <span>Learning pace</span>
            <strong>{report.learningPace || 'Steady pace'}</strong>
          </div>
          <div className='fireflyReportsMetaStat'>
            <span>Topics</span>
            <strong>{report.topicsCovered?.length || 0}</strong>
          </div>
          <div className='fireflyReportsMetaStat'>
            <span>Strength signals</span>
            <strong>{report.strengths?.length || 0}</strong>
          </div>
          <div className='fireflyReportsMetaStat'>
            <span>Next steps</span>
            <strong>{report.recommendedNextSteps?.length || 0}</strong>
          </div>
        </div>

        {report.summary ? (
          <div className='fireflyReportsSummary'>
            <h3>Session summary</h3>
            <p>{report.summary}</p>
          </div>
        ) : null}

        {report.topicsCovered?.length ? (
          <div className='fireflyReportsSection'>
            <h3>Topics covered</h3>
            <div className='fireflyReportsChipRow'>
              {report.topicsCovered.map((topic) => (
                <span key={`${report.id}-${topic}`} className='fireflyReportsChip'>{topic}</span>
              ))}
            </div>
          </div>
        ) : null}

        <div className='fireflyReportsTwoCol'>
          <div className='fireflyReportsSection'>
            <h3>Strengths</h3>
            <ul>
              {(report.strengths?.length ? report.strengths : ['No clear strengths were captured in this session.']).map((item, index) => (
                <li key={`${report.id}-strength-${index}`}>{item}</li>
              ))}
            </ul>
          </div>

          <div className='fireflyReportsSection'>
            <h3>Challenges</h3>
            <ul>
              {(report.struggles?.length ? report.struggles : ['No major challenge was captured in this session.']).map((item, index) => (
                <li key={`${report.id}-struggle-${index}`}>{item}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className='fireflyReportsTwoCol'>
          <div className='fireflyReportsSection'>
            <h3>Recommended next steps</h3>
            <ul>
              {(report.recommendedNextSteps?.length ? report.recommendedNextSteps : ['Start the next session with one concrete problem from this topic.']).map((item, index) => (
                <li key={`${report.id}-next-${index}`}>{item}</li>
              ))}
            </ul>
          </div>

          <div className='fireflyReportsSection'>
            <h3>Teaching methods used</h3>
            <div className='fireflyReportsChipRow'>
              {(report.teachingMethodsUsed?.length ? report.teachingMethodsUsed : ['Adaptive tutoring']).map((item, index) => (
                <span key={`${report.id}-method-${index}`} className='fireflyReportsChip'>{item}</span>
              ))}
            </div>
          </div>
        </div>

        {report.diagnoses?.length ? (
          <div className='fireflyReportsSection'>
            <h3>Fine-grained diagnosis</h3>
            <div className='fireflyReportsDiagnosisList'>
              {report.diagnoses.map((item, index) => (
                <article key={`${report.id}-diagnosis-${index}`} className='fireflyReportsDiagnosisCard'>
                  <div className='fireflyReportsDiagnosisLine'>
                    <span>Topic</span>
                    <strong>{item.topic || 'General concept'}</strong>
                  </div>
                  {item.subtopic ? (
                    <div className='fireflyReportsDiagnosisLine'>
                      <span>Subtopic</span>
                      <strong>{item.subtopic}</strong>
                    </div>
                  ) : null}
                  {item.misconception ? (
                    <div className='fireflyReportsDiagnosisBlock'>
                      <span>Misconception</span>
                      <p>{item.misconception}</p>
                    </div>
                  ) : null}
                  {item.evidence ? (
                    <div className='fireflyReportsDiagnosisBlock'>
                      <span>Evidence</span>
                      <p>{item.evidence}</p>
                    </div>
                  ) : null}
                  {item.bestIntervention ? (
                    <div className='fireflyReportsDiagnosisBlock'>
                      <span>Best intervention</span>
                      <p>{item.bestIntervention}</p>
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          </div>
        ) : null}
      </article>
    )
  }

  return (
    <div className='fireflyReportsPage'>
      <section className='fireflyReportsHero'>
        <div>
          <span className='fireflyReportsEyebrow'>My Firefly Reports</span>
          <h1>Firefly learning intelligence.</h1>
          <p>
            See the big picture across every session, then open individual reports for the exact diagnosis,
            evidence, and next step from each lesson.
          </p>
        </div>

        <button type='button' className='fireflyReportsRefresh' onClick={handleRefreshReports} disabled={refreshingProfile}>
          {refreshingProfile ? 'Rebuilding...' : 'Refresh and rebuild'}
        </button>
      </section>

      {error ? <p className='fireflyReportsError'>{error}</p> : null}

      <div className='fireflyReportsTabs' role='tablist' aria-label='Firefly reports views'>
        {[
          ['overview', 'General report'],
          ['sessions', 'Individual reports'],
          ['topics', 'Topic intelligence'],
        ].map(([view, label]) => (
          <button
            key={view}
            type='button'
            className={activeView === view ? 'active' : ''}
            onClick={() => setActiveView(view)}
          >
            {label}
          </button>
        ))}
      </div>

      {activeView === 'overview' ? (
        <>
          <section className='fireflyReportsProfileCard'>
            <div className='fireflyReportsSectionHead'>
              <div>
                <span className='fireflyReportsTag'>General Report</span>
                <h2>{generatedProfile?.headline || 'Your learning command center'}</h2>
                <p>{generatedProfile?.learnerSnapshot || `Aggregated from ${reports.length} Firefly ${reports.length === 1 ? 'session' : 'sessions'}.`}</p>
              </div>
            </div>

            <div className='fireflyReportsMetaGrid fireflyReportsMetricGrid'>
              <div className='fireflyReportsMetric fireflyReportsMetricSessions'>
                <span>Reviewed sessions</span>
                <strong>{generatedProfile?.sourceCount || reports.length}</strong>
              </div>
              <div className='fireflyReportsMetric fireflyReportsMetricEvidence'>
                <span>Evidence strength</span>
                <strong>{generatedProfile?.evidenceStrength || 'live'}</strong>
              </div>
              <div className='fireflyReportsMetric fireflyReportsMetricReadiness'>
                <span>Readiness band</span>
                <strong>{generatedProfile?.readinessBand || 'building profile'}</strong>
              </div>
              <div className='fireflyReportsMetric fireflyReportsMetricAnalysis'>
                <span>Last analysis</span>
                <strong>{generatedProfile?.lastAnalyzedLabel || generatedProfile?.updatedLabel || formatDateTime(reports[0]?.endedAtMs || reports[0]?.startedAtMs)}</strong>
              </div>
            </div>

            {generatedProfile?.evidenceSummary ? (
              <div className='fireflyReportsSummary'>
                <h3>Profile freshness</h3>
                <p>{generatedProfile.evidenceSummary}</p>
              </div>
            ) : null}

            {generatedProfile?.fireflyRecommendation?.shouldInviteLiveSession ? (
              <div className='fireflyReportsSummary'>
                <h3>Firefly support recommendation</h3>
                <p>{generatedProfile.fireflyRecommendation.studentSummary || 'Firefly is recommending a live support session based on your recent learning signals.'}</p>
                {generatedProfile.fireflyRecommendation.focusConcepts.length ? (
                  <ul className='fireflyReportsMiniList'>
                    {generatedProfile.fireflyRecommendation.focusConcepts.map((item) => (
                      <li key={`firefly-support-${item.label}`}>{item.label}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </section>

          <section className='fireflyReportsChartGrid'>
            <article className='fireflyReportsChartPanel fireflyReportsChartPanelWide'>
              <div className='fireflyReportsSectionHead'>
                <div>
                  <span className='fireflyReportsTag'>Trend</span>
                  <h2>Learning rhythm</h2>
                </div>
              </div>
              <div className='fireflyReportsChartFrame'>
                <Line data={rhythmData} options={lineOptions} />
              </div>
            </article>

            <article className='fireflyReportsChartPanel'>
              <div className='fireflyReportsSectionHead'>
                <div>
                  <span className='fireflyReportsTag'>Signals</span>
                  <h2>Strengths and gaps</h2>
                </div>
              </div>
              <div className='fireflyReportsChartFrame'>
                <Doughnut data={signalData} options={doughnutOptions} />
              </div>
            </article>

            <article className='fireflyReportsChartPanel'>
              <div className='fireflyReportsSectionHead'>
                <div>
                  <span className='fireflyReportsTag'>Topics</span>
                  <h2>Most visited concepts</h2>
                </div>
              </div>
              <div className='fireflyReportsChartFrame'>
                <Bar data={topicData} options={barOptions} />
              </div>
            </article>

            <article className='fireflyReportsChartPanel'>
              <div className='fireflyReportsSectionHead'>
                <div>
                  <span className='fireflyReportsTag'>Teaching</span>
                  <h2>{generatedProfile?.learningModes?.length ? 'Learning mode fit' : 'Preferred modes'}</h2>
                </div>
              </div>
              <div className='fireflyReportsChartFrame'>
                <Bar data={modeData} options={barOptions} />
              </div>
            </article>
          </section>

          {generatedProfile ? (
            <section className='fireflyReportsProfileCard'>
              <div className='fireflyReportsSectionHead'>
                <div>
                  <span className='fireflyReportsTag'>Adaptive Profile</span>
                  <h2>What changed and how to learn better next</h2>
                  <p>{generatedProfile.futureOfLearningLens || generatedProfile.momentumNarrative || 'This profile updates itself when a new Firefly report changes the pattern of evidence.'}</p>
                </div>
              </div>

              <div className='fireflyReportsTwoCol fireflyReportsGeneratedGrid'>
                <div className='fireflyReportsSection fireflyReportsSoftCard'>
                  <h3>What changed since the last update</h3>
                  <p>{generatedProfile.changeSinceLastUpdate.summary || 'As new sessions shift the evidence, this section will explain exactly what improved, what appeared, and what needs closer support.'}</p>
                  <div className='fireflyReportsChipGroup'>
                    {generatedProfile.changeSinceLastUpdate.newStrengths.map((item) => (
                      <span key={`new-strength-${item}`} className='fireflyReportsChip fireflyReportsChipSuccess'>{item}</span>
                    ))}
                    {generatedProfile.changeSinceLastUpdate.improvedSignals.map((item) => (
                      <span key={`improved-${item}`} className='fireflyReportsChip fireflyReportsChipNeutral'>{item}</span>
                    ))}
                    {generatedProfile.changeSinceLastUpdate.newRisks.map((item) => (
                      <span key={`new-risk-${item}`} className='fireflyReportsChip fireflyReportsChipWarn'>{item}</span>
                    ))}
                    {generatedProfile.changeSinceLastUpdate.declinedSignals.map((item) => (
                      <span key={`declined-${item}`} className='fireflyReportsChip fireflyReportsChipWarn'>{item}</span>
                    ))}
                  </div>
                </div>

                <div className='fireflyReportsSection fireflyReportsSoftCard'>
                  <h3>How Firefly helps you learn best</h3>
                  <p>{generatedProfile.sessionIntelligence.engagementPattern || generatedProfile.sessionIntelligence.pacePattern || 'The tutoring profile is still identifying the best way to pace and frame explanations for you.'}</p>
                  <div className='fireflyReportsChipGroup'>
                    {generatedProfile.sessionIntelligence.preferredTeachingModes.map((item) => (
                      <span key={`mode-${item}`} className='fireflyReportsChip'>{item}</span>
                    ))}
                    {generatedProfile.sessionIntelligence.explanationFormatsThatWork.map((item) => (
                      <span key={`format-${item}`} className='fireflyReportsChip fireflyReportsChipAlt'>{item}</span>
                    ))}
                  </div>
                </div>

                <div className='fireflyReportsSection fireflyReportsSoftCard'>
                  <h3>What is going well</h3>
                  <ul>
                    {(generatedProfile.studentFacing.whatIsGoingWell.length
                      ? generatedProfile.studentFacing.whatIsGoingWell
                      : ['Your strongest recurring wins will appear here as Firefly sees the same positive patterns more often.']).map((item, index) => (
                      <li key={`well-${index}`}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div className='fireflyReportsSection fireflyReportsSoftCard'>
                  <h3>Focus for the next sessions</h3>
                  <ul>
                    {(generatedProfile.studentFacing.focusNext.length
                      ? generatedProfile.studentFacing.focusNext
                      : ['Your next session focus will appear here once Firefly sees a stable pattern in the evidence.']).map((item, index) => (
                      <li key={`focus-${index}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className='fireflyReportsTwoCol'>
                <div className='fireflyReportsSection'>
                  <h3>Strongest themes</h3>
                  <div className='fireflyReportsDiagnosisList'>
                    {(generatedProfile.conceptMasteryMap.strongestThemes.length
                      ? generatedProfile.conceptMasteryMap.strongestThemes
                      : [{ label: 'Still building profile', evidence: 'Firefly needs more repeated evidence to lock in your strongest themes.' }]).map((item, index) => (
                      <article key={`strong-theme-${index}`} className='fireflyReportsDiagnosisCard'>
                        <div className='fireflyReportsDiagnosisBlock'>
                          <span>Theme</span>
                          <p>{item.label}</p>
                        </div>
                        {item.evidence ? (
                          <div className='fireflyReportsDiagnosisBlock'>
                            <span>Evidence</span>
                            <p>{item.evidence}</p>
                          </div>
                        ) : null}
                      </article>
                    ))}
                  </div>
                </div>

                <div className='fireflyReportsSection'>
                  <h3>Fragile themes</h3>
                  <div className='fireflyReportsDiagnosisList'>
                    {(generatedProfile.conceptMasteryMap.fragileThemes.length
                      ? generatedProfile.conceptMasteryMap.fragileThemes
                      : [{ label: 'No fragile theme locked in yet', evidence: 'When a concept keeps needing support, it will appear here.' }]).map((item, index) => (
                      <article key={`fragile-theme-${index}`} className='fireflyReportsDiagnosisCard'>
                        <div className='fireflyReportsDiagnosisBlock'>
                          <span>Theme</span>
                          <p>{item.label}</p>
                        </div>
                        {item.evidence ? (
                          <div className='fireflyReportsDiagnosisBlock'>
                            <span>Evidence</span>
                            <p>{item.evidence}</p>
                          </div>
                        ) : null}
                      </article>
                    ))}
                  </div>
                </div>
              </div>

              <div className='fireflyReportsTwoCol'>
                <div className='fireflyReportsSection fireflyReportsSoftCard'>
                  <h3>Your next session plan</h3>
                  <div className='fireflyReportsChipGroup'>
                    {generatedProfile.studentActionPlan.keepDoing.map((item) => (
                      <span key={`keep-${item}`} className='fireflyReportsChip'>{item}</span>
                    ))}
                    {generatedProfile.studentActionPlan.focusNextSessions.map((item) => (
                      <span key={`next-session-${item}`} className='fireflyReportsChip fireflyReportsChipAlt'>{item}</span>
                    ))}
                    {generatedProfile.studentActionPlan.beforeNextSession.map((item) => (
                      <span key={`before-next-${item}`} className='fireflyReportsChip fireflyReportsChipNeutral'>{item}</span>
                    ))}
                  </div>
                  {generatedProfile.studentActionPlan.nextChallenge ? (
                    <p className='fireflyReportsDetailLine'><strong>Next challenge:</strong> {generatedProfile.studentActionPlan.nextChallenge}</p>
                  ) : null}
                </div>

                <div className='fireflyReportsSection fireflyReportsSoftCard'>
                  <h3>Student-facing note</h3>
                  <p>{generatedProfile.studentFacing.encouragement || 'Keep using Firefly around one specific gap at a time so the tutoring profile can sharpen faster.'}</p>
                  {generatedProfile.studentFacing.whyThisMatters ? (
                    <p className='fireflyReportsDetailLine'><strong>Why this matters:</strong> {generatedProfile.studentFacing.whyThisMatters}</p>
                  ) : null}
                  {(generatedProfile.studentFacing.reflectionPrompt || generatedProfile.studentActionPlan.reflectionPrompt) ? (
                    <p className='fireflyReportsDetailLine'><strong>Reflection prompt:</strong> {generatedProfile.studentFacing.reflectionPrompt || generatedProfile.studentActionPlan.reflectionPrompt}</p>
                  ) : null}
                </div>
              </div>
            </section>
          ) : profile ? (
            <section className='fireflyReportsProfileCard'>
              <div className='fireflyReportsSectionHead'>
                <div>
                  <span className='fireflyReportsTag'>Learner Profile</span>
                  <h2>How Firefly currently understands you</h2>
                </div>
              </div>

              <div className='fireflyReportsMetaGrid'>
                <div className='fireflyReportsMetaStat'>
                  <span>Pace</span>
                  <strong>{profile.learningPace || 'Still learning your pace'}</strong>
                </div>
                <div className='fireflyReportsMetaStat'>
                  <span>Confidence trend</span>
                  <strong>{profile.confidenceTrend || 'Building a signal'}</strong>
                </div>
                <div className='fireflyReportsMetaStat'>
                  <span>Preferred modes</span>
                  <strong>{profile.preferredTeachingModes?.length ? profile.preferredTeachingModes.join(', ') : 'Detecting best mode'}</strong>
                </div>
                <div className='fireflyReportsMetaStat'>
                  <span>Topics seen</span>
                  <strong>{profile.topicsSeen?.length || 0}</strong>
                </div>
              </div>

              {profile.summary ? (
                <div className='fireflyReportsSummary'>
                  <h3>Profile summary</h3>
                  <p>{profile.summary}</p>
                </div>
              ) : null}

              <div className='fireflyReportsTwoCol'>
                <div className='fireflyReportsSection'>
                  <h3>Strengths</h3>
                  <ul>
                    {(profile.strengths?.length ? profile.strengths : ['Firefly is still building a stronger strengths profile over the next few sessions.']).map((item, index) => (
                      <li key={`profile-strength-${index}`}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div className='fireflyReportsSection'>
                  <h3>Challenges</h3>
                  <ul>
                    {(profile.struggles?.length ? profile.struggles : ['No major challenge pattern has been locked in yet.']).map((item, index) => (
                      <li key={`profile-struggle-${index}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>
          ) : null}
        </>
      ) : null}

      {activeView === 'sessions' ? (
        <section className='fireflyReportsSessionsView'>
          <div className='fireflyReportsSectionHead'>
            <div>
              <span className='fireflyReportsTag'>Individual Reports</span>
              <h2>Open any Firefly session</h2>
              <p>Pick a report to open it in a focused full-screen reading view.</p>
            </div>
          </div>

          {!reports.length ? (
            <div className='fireflyReportsEmpty'>
              <h3>No reports yet</h3>
              <p>Finish a Firefly tutoring session and your learning report will appear here.</p>
            </div>
          ) : (
            <>
              {selectedReport ? (
                <div className='fireflyReportsFullReader'>
                  <button
                    type='button'
                    className='fireflyReportsBackButton'
                    onClick={() => setSelectedReportId('')}
                  >
                    Back to report list
                  </button>
                  {renderReportDetail(selectedReport)}
                </div>
              ) : (
                <div className='fireflyReportsSessionLauncher'>
                  {reports.map((report) => (
                    <button
                      key={report.id}
                      type='button'
                      className='fireflyReportsLaunchTile'
                      onClick={() => setSelectedReportId(report.id)}
                    >
                      <span className='fireflyReportsLaunchDate'>{formatShortDate(report.endedAtMs || report.startedAtMs)}</span>
                      <span className='fireflyReportsLaunchBody'>
                        <strong>{report.sessionFocus || 'General Firefly lesson'}</strong>
                        <small>{formatDateTime(report.endedAtMs || report.startedAtMs)}</small>
                      </span>
                      <span className='fireflyReportsLaunchMetaLine'>
                        <span>{formatDuration(report.durationSeconds)}</span>
                        <span>{report.learningPace || 'steady pace'}</span>
                        <span>{report.topicsCovered?.length || 0} topics</span>
                      </span>
                      <span className='fireflyReportsLaunchTopics'>
                        {(report.topicsCovered?.length ? report.topicsCovered.slice(0, 3) : ['General lesson']).map((topic) => (
                          <span key={`${report.id}-launch-${topic}`}>{topic}</span>
                        ))}
                      </span>
                      <span className='fireflyReportsLaunchAction'>Open report</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      ) : null}

      {activeView === 'topics' ? (
        <section className='fireflyReportsHistory'>
          <div className='fireflyReportsSectionHead'>
            <div>
              <span className='fireflyReportsTag'>Topic Intelligence</span>
              <h2>What Firefly should keep tracking</h2>
              <p>Topic frequency, misconceptions, and interventions collected across your sessions.</p>
            </div>
          </div>

          <div className='fireflyReportsTopicGrid'>
            {(analytics.topTopics.length ? analytics.topTopics : [{ label: 'No topics yet', value: 0 }]).map((topic) => (
              <article key={topic.label} className='fireflyReportsTopicCard'>
                <div>
                  <span>Concept</span>
                  <h3>{topic.label}</h3>
                </div>
                <strong>{topic.value}</strong>
              </article>
            ))}
          </div>

          {profile?.diagnoses?.length ? (
            <div className='fireflyReportsSection'>
              <h3>Current diagnosis map</h3>
              <div className='fireflyReportsDiagnosisList'>
                {profile.diagnoses.map((item, index) => (
                  <article key={`profile-diagnosis-${index}`} className='fireflyReportsDiagnosisCard'>
                    <div className='fireflyReportsDiagnosisLine'>
                      <span>Topic</span>
                      <strong>{item.topic || 'General concept'}</strong>
                    </div>
                    {item.subtopic ? (
                      <div className='fireflyReportsDiagnosisLine'>
                        <span>Subtopic</span>
                        <strong>{item.subtopic}</strong>
                      </div>
                    ) : null}
                    {item.misconception ? (
                      <div className='fireflyReportsDiagnosisBlock'>
                        <span>Misconception</span>
                        <p>{item.misconception}</p>
                      </div>
                    ) : null}
                    {item.bestIntervention ? (
                      <div className='fireflyReportsDiagnosisBlock'>
                        <span>Best intervention</span>
                        <p>{item.bestIntervention}</p>
                      </div>
                    ) : null}
                  </article>
                ))}
              </div>
            </div>
          ) : (
            <div className='fireflyReportsEmpty'>
              <h3>No diagnosis map yet</h3>
              <p>After a few focused sessions, Firefly will start showing topic-level misconceptions and interventions here.</p>
            </div>
          )}
        </section>
      ) : null}
    </div>
  )
}

export default FireflyReportsComp
